import { useState, useEffect } from "react";
import { MenuOutlined } from "@ant-design/icons";
import MobileMenu from "./MobileMenu";
import { showModal } from "@/store/slices/modalSlice";
import ResetPasswordModal from "@/features/auth/resetPassword/resetPassword";
import DesktopMenu from "./DesktopMenu";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";
import { useDispatch, shallowEqual, useSelector } from "react-redux";
import {
  currencyRateListRequest,
  userLocationRequest
} from "@/features/auth/duck/auth";
import {
  explorePropertyRequest,
  propertySignatureRequest,
  countryBasedPropertyRequest
} from "@/features/property/duck/property";
import { userRequest } from "@/features/user/duck/user";
import SignUpForm from "@/features/auth/signup/SignUp";
import TopNavbar from "@/common/organisms/Navbar/TopNavbar";
import "./Navbar.css";
import { Grid, notification } from "antd";
import {
  getPaymentCancelMessage,
  getPaymentSuccessMessage
} from "@/constants/miscConstants";
import { useTranslation } from "react-i18next";
import { LanguageDropdown } from "@/common";
import { getUserSubscriptionRequest } from "@/features/user/duck/userSubscription";
import { getConfig } from "@/get-config";

const Navbar = () => {
  // State to manage the navbar's visibility
  const [nav, setNav] = useState(false);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { cdnAssetUrl } = getConfig();

  const { isLogin, authToken, userId, userInfo } = useSelector(
    (state) => ({
      isLogin: state.auth.isLogin,
      authToken: state.auth.token,
      userId: state.auth.userId,
      userInfo: state.user.userInfo
    }),
    shallowEqual
  );

  const isResetModal = searchParams.get("resetModal");
  const isOpenSignupModal = searchParams.get("signup");
  const token = searchParams.get("token");

  const paymentSuccessful = searchParams.get("paymentSuccessful");
  const paymentCancelled = searchParams.get("paymentCancelled");

  useEffect(() => {
    if (paymentSuccessful) {
      notification.success(getPaymentSuccessMessage(t));
    }
    if (paymentCancelled) {
      notification.error(getPaymentCancelMessage(t));
    }
    if (isResetModal) {
      showResetPasswordModal();
    }

    if (!!isOpenSignupModal && !isLogin) {
      handleOpenSignupModal();
    }

    if (
      paymentSuccessful ||
      paymentCancelled ||
      isResetModal ||
      isOpenSignupModal
    ) {
      navigate(
        {
          pathname: location.pathname
        },
        { replace: true }
      );
    }
  }, [paymentSuccessful, paymentCancelled, isResetModal, isOpenSignupModal]);

  useEffect(() => {
    dispatch(currencyRateListRequest());
    dispatch(explorePropertyRequest({ params: { size: 12 } }));
    dispatch(
      propertySignatureRequest({
        params: { size: 10, isSignature: true },
        isPagination: false
      })
    );
    dispatch(countryBasedPropertyRequest());
  }, [userInfo]);

  useEffect(() => {
    if (authToken && !userInfo) {
      dispatch(userRequest({ userId }));
      dispatch(
        getUserSubscriptionRequest({
          uuid: userId
        })
      );
    }
  }, [authToken]);

  useEffect(() => {
    dispatch(userLocationRequest());
  }, []);

  // Toggle function to handle the navbar's display
  const handleNav = () => {
    setNav(!nav);
  };

  const showResetPasswordModal = () => {
    dispatch(
      showModal({
        content: <ResetPasswordModal token={token} />,
        classes: "login-modal-container"
      })
    );
  };
  const handleOpenSignupModal = () => {
    dispatch(
      showModal({
        content: <SignUpForm />,
        classes: "sign-up-modal-container"
      })
    );
  };

  const handleNavItemClick = (e, item) => {
    const restrictedRoutes = ["/manage/add-property"];
    const isRestricted = restrictedRoutes.includes(item.to);

    if (isRestricted && !isLogin) {
      e.preventDefault();
      handleOpenSignupModal();
      return;
    }
  };

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isMobile = !screens.lg;

  return (
    <nav className="mx-auto w-full">
      {!isMobile && <TopNavbar />}
      <div className="mx-auto w-full max-w-screen-xl h-20 flex justify-between items-center p-3 md:p-5">
        {/* Logo */}
        <Link
          to="/"
          className="absolute left-[40%] rtl:right-[0%] transform -translate-x-[40%] lg:static lg:transform-none focus:outline-none">
          <img
            className="object-contain image-rendering-crisp-edges h-20 w-36 md:w-[270px] select-none border-0 outline-none"
            src={`${cdnAssetUrl}logoDashboard.svg`}
            loading="lazy"
            alt="Homes Globe"
            draggable={false}
          />
        </Link>

        {!isMobile && (
          <DesktopMenu
            isLogin={isLogin}
            userInfo={userInfo}
            handleClick={handleNavItemClick}
          />
        )}
        {isMobile && (
          <>
            {/* Mobile Navigation Icon */}
            <div onClick={handleNav} className="block cursor-pointer">
              {nav ? (
                <MenuOutlined className="text-secondary text-xl" />
              ) : (
                <MenuOutlined className="text-secondary text-2xl" />
              )}
            </div>

            {/* Mobile Navigation Menu */}
            <MobileMenu
              nav={nav}
              onClose={handleNav}
              isLogin={isLogin}
              userInfo={userInfo}
              isMobile={isMobile}
              handleClick={handleNavItemClick}
            />
            <LanguageDropdown className="custom-select-currency" />
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
